/**
 * Session-scoped log of schema drift events captured by the `lenient()`
 * helper in `lenient.ts`. Lets an admin "drift" view show "what drift
 * has this tab seen so far" without round-tripping Sentry's read API.
 *
 * Trade-off: session-only. A drift event that fired in a previous tab,
 * or on another operator's device, won't appear here.
 *
 * Capacity: bounded — we keep the last `MAX_ENTRIES` events FIFO so a
 * misbehaving doc that fires thousands of drift events doesn't grow
 * memory unboundedly. The dashboard cares about distinct hints + sample
 * values, not exhaustive history.
 */

export interface DriftEvent {
  /** `payment.method`, `court.location`, etc. — the field-path hint
   *  passed into `lenient()`. */
  hint: string
  /** The offending value, JSON-stringified so it's printable. */
  inputJson: string
  /** What the field was coerced to. */
  fallbackJson: string
  /** Wall-clock time of the first occurrence. */
  firstSeenAt: Date
  /** Wall-clock time of the most recent occurrence. */
  lastSeenAt: Date
  /** How many times this exact (hint, inputJson) pair has been seen. */
  count: number
}

const MAX_ENTRIES = 200
type Key = string

function keyOf(hint: string, inputJson: string): Key {
  return `${hint}|${inputJson}`
}

const store = new Map<Key, DriftEvent>()
const listeners = new Set<() => void>()

export function recordDrift(hint: string, input: unknown, fallback: unknown): void {
  const inputJson = safeJson(input)
  const fallbackJson = safeJson(fallback)
  const key = keyOf(hint, inputJson)
  const now = new Date()
  const prev = store.get(key)
  if (prev) {
    prev.lastSeenAt = now
    prev.count += 1
  } else {
    if (store.size >= MAX_ENTRIES) {
      // FIFO eviction — drop the oldest key.
      const oldestKey = store.keys().next().value
      if (oldestKey !== undefined) store.delete(oldestKey)
    }
    store.set(key, {
      hint,
      inputJson,
      fallbackJson,
      firstSeenAt: now,
      lastSeenAt: now,
      count: 1,
    })
  }
  listeners.forEach((l) => l())
}

export function getDriftEvents(): DriftEvent[] {
  // Most-recent first; the dashboard renders them in that order.
  return Array.from(store.values()).sort(
    (a, b) => b.lastSeenAt.getTime() - a.lastSeenAt.getTime(),
  )
}

export function clearDriftEvents(): void {
  store.clear()
  listeners.forEach((l) => l())
}

export function subscribeDrift(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}
